import React, { useState, useEffect, useRef, useCallback } from 'react';

export const FullscreenViewer = ({ 
  imageUrl, 
  title, 
  index = 0, 
  total = 1, 
  onNext, 
  onPrev, 
  onClose 
}) => {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Полноэкранный режим
  useEffect(() => {
    const el = containerRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => {});
    return () => {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []);

  // Выход при системном закрытии fullscreen
  useEffect(() => {
    const handleChange = () => { if (!document.fullscreenElement) onClose(); };
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, [onClose]);

  // Сброс зума при смене фото
  useEffect(() => { resetZoom(); }, [imageUrl]);

  const resetZoom = useCallback(() => {
    setZoom(1); setPan({ x: 0, y: 0 }); setIsDragging(false);
  }, []);

  const zoomIn = () => setZoom(z => Math.min(z + 0.25, 6));
  const zoomOut = () => setZoom(z => Math.max(z - 0.25, 0.25));

  const handleWheel = (e) => { e.preventDefault(); e.deltaY < 0 ? zoomIn() : zoomOut(); };

  const handleMouseDown = (e) => {
    if (zoom <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    if (containerRef.current) containerRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    const img = imgRef.current;
    const container = containerRef.current;
    if (img && container) {
      const cRect = container.getBoundingClientRect();
      const iRect = img.getBoundingClientRect();
      const maxX = Math.max(0, (iRect.width - cRect.width) / 2) + 50;
      const maxY = Math.max(0, (iRect.height - cRect.height) / 2) + 50;
      setPan({ x: Math.max(-maxX, Math.min(maxX, newX)), y: Math.max(-maxY, Math.min(maxY, newY)) });
    } else {
      setPan({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (containerRef.current) containerRef.current.style.cursor = zoom > 1 ? 'grab' : 'zoom-in';
  };

  // Горячие клавиши
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === '+' || e.key === '=') zoomIn();
      else if (e.key === '-') zoomOut();
      else if (e.key === '0') resetZoom();
      else if (e.key === 'ArrowRight' && onNext) onNext();
      else if (e.key === 'ArrowLeft' && onPrev) onPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onNext, onPrev, zoomIn, zoomOut, resetZoom]);

  const btnStyle = {
    background: 'rgba(201, 169, 110, 0.15)',
    border: '1px solid rgba(201, 169, 110, 0.4)',
    color: 'var(--text-light)',
    width: 38, height: 38,
    cursor: 'pointer', borderRadius: 8,
    fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'all 0.2s'
  };

  return (
    <div
      ref={containerRef}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(5, 5, 3, 0.96)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        overflow: 'hidden', cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
        zIndex: 100000
      }}
      onWheel={handleWheel} onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp} onClick={(e) => e.target === containerRef.current && onClose()}
    >
      <img ref={imgRef} src={imageUrl} alt={title || 'Фото'} loading="eager" style={{
        maxWidth: '100vw', maxHeight: '100vh', objectFit: 'contain',
        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
        transition: isDragging ? 'none' : 'transform 0.2s ease-out',
        filter: 'contrast(1.05) sepia(0.1)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        borderRadius: '4px', userSelect: 'none', WebkitUserDrag: 'none'
      }} />

      {/* Панель управления */}
      <div style={{ position: 'absolute', top: 20, right: 20, display: 'flex', gap: 8, zIndex: 100001 }}>
        <button onClick={zoomOut} style={btnStyle}>−</button>
        <span style={{ ...btnStyle, fontSize: '0.8rem', cursor: 'default' }}>{Math.round(zoom * 100)}%</span>
        <button onClick={zoomIn} style={btnStyle}>+</button>
        <button onClick={resetZoom} style={btnStyle}>⟲</button>
        <button onClick={onClose} style={{ ...btnStyle, background: 'rgba(231, 76, 60, 0.8)' }}>✕</button>
      </div>

      {/* Стрелки навигации */}
      {onPrev && (
        <button onClick={onPrev} style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(201,169,110,0.15)', border: '1px solid rgba(201,169,110,0.4)', color: 'var(--text-light)', width: 50, height: 50, cursor: 'pointer', borderRadius: '50%', fontSize: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          ‹
        </button>
      )}
      {onNext && (
        <button onClick={onNext} style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(201,169,110,0.15)', border: '1px solid rgba(201,169,110,0.4)', color: 'var(--text-light)', width: 50, height: 50, cursor: 'pointer', borderRadius: '50%', fontSize: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          ›
        </button>
      )}

      {/* Счётчик и название */}
      <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', textAlign: 'center', pointerEvents: 'none' }}>
        <div style={{ fontFamily: 'Special Elite, monospace', fontSize: '0.9rem', color: 'var(--accent)', letterSpacing: '3px', marginBottom: 8 }}>
          {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </div>
        {title && (
          <div style={{ fontFamily: 'Special Elite, monospace', fontSize: '0.85rem', color: 'var(--text-light)', background: 'rgba(10,10,8,0.85)', padding: '6px 18px', borderRadius: '20px', letterSpacing: '1.5px', border: '1px solid rgba(201,169,110,0.2)' }}>
            {title}
          </div>
        )}
      </div>
    </div>
  );
};